import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type ApiError, type GetDealBoardResult } from '@whinerp/client';
import {
  AvatarStack,
  EmptyState,
  ErrorState,
  FilterChips,
  KanbanBoard,
  Num,
  SectionLabel,
  Skeleton,
  type BoardColumn,
} from '@whinerp/ui';
import { formatCount, formatMoney, formatRelativeDate } from '@whinerp/contracts/format';
import { useFormat, useT } from '../../app/i18n.js';
import { defineScreen } from '../types.js';
import { PipelineFunnelPanel } from './pipeline-funnel-panel.js';
import { DealAnalysisPanel } from './deal-analysis-panel.js';

/**
 * SC-12 Deals board.
 *
 * The column headers carry a count and a total that came from the server's
 * count() and sum() over the same definition that produced the cards - with
 * sharing applied in its FROM clause. That last part is not a detail: a column
 * total computed any other way would include deals the reader cannot open,
 * which turns a board header into an oracle for records they are not allowed
 * to see.
 *
 * A card carries four facts without being opened: name, company, amount and
 * close date, with the owner and the health chip beside them.
 */

type Board = GetDealBoardResult;

function DealsBoardScreen(): JSX.Element {
  const t = useT();
  const format = useFormat();
  const queryClient = useQueryClient();
  const [pipelineId, setPipelineId] = useState<string | null>(null);
  const [openOnly, setOpenOnly] = useState(false);

  const pipelines = useQuery({
    queryKey: ['pipelines'],
    queryFn: () => api.listPipelines({}),
  });

  const board = useQuery<Board, ApiError>({
    queryKey: ['deal-board', pipelineId],
    queryFn: () => api.getDealBoard({ query: pipelineId ? { pipelineId } : {} }),
    /*
     * The board that is on screen stays there while the next pipeline loads.
     *
     * Without this the early return below fires on every pipeline change and
     * takes the pipeline selector with it - the control is inside the region
     * its own query gates. Correct on a first load, wrong on a refetch, and
     * the two are written identically (P-27).
     */
    placeholderData: keepPreviousData,
  });

  /*
   * Today's shape, recorded once per mount (G-091).
   *
   * There is no scheduler, so the board is what makes history exist. A ref
   * rather than a query because this writes, and React's development
   * double-mount would otherwise fire it twice - harmless here, since the day
   * is overwritten rather than appended, but a write fired twice by a
   * rendering strategy is a habit that stops being harmless when it is copied.
   *
   * Failure is deliberately silent. Somebody opening the board came to work
   * deals; a banner about a background capture would sit on top of the work,
   * and the board is unaffected by it.
   */
  const captured = useRef(false);
  const capture = useMutation({ mutationFn: () => api.capturePipelineSnapshot({}) });
  const takeSnapshot = capture.mutate;
  useEffect(() => {
    if (captured.current) return;
    captured.current = true;
    takeSnapshot();
  }, [takeSnapshot]);

  const move = useMutation<unknown, ApiError, { dealId: string; stageId: string; closeReason?: string }>({
    mutationFn: (input: { dealId: string; stageId: string; closeReason?: string }) =>
      api.moveDeal({
        path: { dealId: input.dealId },
        body: { stageId: input.stageId, ...(input.closeReason ? { closeReason: input.closeReason } : {}) },
      }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['deal-board'] }),
    /*
     * A refused move has to say so (F-043).
     *
     * A stage can insist on fields before a deal leaves it, and without this
     * the card springs back to where it was and nothing explains why - which
     * reads as the board being broken rather than as the rule the workspace
     * asked for.
     */
    onError: () => void queryClient.invalidateQueries({ queryKey: ['deal-board'] }),
  });

  const cardName = useMemo(() => {
    const names = new Map<string, string>();
    for (const column of board.data?.columns ?? []) {
      for (const deal of column.deals) names.set(deal.dealId, deal.name);
    }
    return names;
  }, [board.data]);

  if (board.isPending) return <DealsBoardLoading />;
  if (board.isError) return <DealsBoardError error={board.error} retry={() => void board.refetch()} />;

  const columns: BoardColumn[] = board.data.columns
    // Filtering hides whole columns rather than emptying them: a won column
    // showing zero of zero when somebody asked for open deals only is a column
    // pretending to be an answer.
    .filter((column) => !openOnly || column.outcome === 'open')
    .map((column) => ({
    id: column.stageId,
    label: column.label,
    // Straight from the server. This component never adds up its own cards.
    count: formatCount(column.count, format),
    totals: column.totals.map((entry) =>
      formatMoney(entry.total, format, { currency: entry.currency }),
    ),
    outcome: column.outcome,
    cards: column.deals.map((deal) => ({
      id: deal.dealId,
      href: `/deals/${deal.dealId}`,
      content: (
        <span className="flex flex-col">
          <span className="truncate text-body font-medium text-ink">{deal.name}</span>
          <span className="truncate pt-0.5 text-dense text-ink-meta">{deal.accountName ?? ''}</span>
          <span className="flex items-baseline justify-between gap-2 pt-2">
            <Num className="text-body text-ink">
              {formatMoney(deal.amount, format, { currency: deal.currency })}
            </Num>
            <Num className="text-dense text-ink-meta">
              {deal.closeDate ? formatRelativeDate(`${deal.closeDate}T12:00:00Z`, format) : ''}
            </Num>
          </span>
          {/*
            * The owner, under a rule, as a monogram rather than a repeated
            * full name. Four cards in a column each spelling out "Demo
            * Administrator" is most of the column's text saying the same
            * thing; the name stays as the title so it is still readable and
            * announced.
            */}
          {deal.ownerName ? (
            <span className="mt-2.5 flex items-center gap-2 border-t border-t-rule pt-2.5">
              <AvatarStack people={[{ id: deal.dealId, name: deal.ownerName }]} />
              <span className="truncate text-dense text-ink-meta">{deal.ownerName}</span>
            </span>
          ) : null}
        </span>
      ),
    })),
  }));

  return (
    <div data-component="DealsBoard" className="flex w-full flex-col gap-4">
      {/*
        Why a move was refused, where the move happened (F-043).

        A stage can insist on fields before a deal leaves it. Without this the
        card springs back to where it was and nothing explains why, which reads
        as the board being broken rather than as the rule the workspace asked
        for - and the rep tries again, twice, before asking somebody.
      */}
      {move.isError ? (
        <p data-board-refusal className="rounded-control bg-danger-tint px-3 py-2 text-dense text-danger-text">
          {move.error.message}
        </p>
      ) : null}

      <header className="flex flex-wrap items-center gap-3">
        <SectionLabel>{t('object.deal.plural')}</SectionLabel>

        {/* The pipeline selector. */}
        <label className="flex items-center gap-2 text-dense text-ink">
          <span className="sr-only">{t('deal.pipeline')}</span>
          <select
            data-pipeline-selector
            aria-label={t('deal.pipeline')}
            value={pipelineId ?? board.data.pipelineId}
            onChange={(event) => setPipelineId(event.target.value)}
            className="h-input rounded-control border border-rule bg-surface px-2 text-dense text-ink"
          >
            {(pipelines.data?.pipelines ?? []).map((pipeline) => (
              <option key={pipeline.pipelineId} value={pipeline.pipelineId}>
                {pipeline.label}
              </option>
            ))}
          </select>
        </label>

        {/*
          * Board / list, and the list is a different SCREEN.
          *
          * It used to be a second view drawn here: the same board cards
          * stacked in a `<ul>`, with no sort, no search, no pagination and no
          * total. It looked like a list and answered none of the questions a
          * list exists for - which close this quarter, what the biggest open
          * deal is - so somebody who pressed it got the board again in one
          * column.
          *
          * SC-57 is the real one, built on RecordList like every other list in
          * the product.
          */}
        <span className="flex items-center gap-1" role="group" aria-label={t('deal.board')}>
          <span
            data-view-toggle="board"
            aria-current="page"
            className="inline-flex h-touch items-center rounded-control bg-bronze-tint px-3 text-dense text-bronze-text"
          >
            {t('deal.board')}
          </span>
          <Link
            data-view-toggle="list"
            to="/deals/list"
            className="inline-flex h-touch items-center rounded-control px-3 text-dense text-ink-600 hover:bg-sunken"
          >
            {t('deal.list')}
          </Link>
        </span>

        <span className="ms-auto flex items-baseline gap-2">
          <Num className="text-meta text-ink-meta">
            {t('list.status.records', {
              values: { count: formatCount(board.data.totalCount, format) },
            })}
          </Num>
          {/* The board's own totals, one per currency. */}
          <span className="flex flex-col items-end">
            {board.data.totals.length === 0 ? (
              <Num className="font-serif text-h2 text-ink-300">{t('deal.noTotal')}</Num>
            ) : (
              board.data.totals.map((entry) => (
                <Num key={entry.currency} className="text-h2 text-ink">
                  {formatMoney(entry.total, format, { currency: entry.currency })}
                </Num>
              ))
            )}
          </span>
        </span>
      </header>

      {/* The board's own filters, removable one at a time. */}
      <FilterChips
        chips={openOnly ? [{ key: 'open', label: t('deal.openOnly'), onRemove: () => setOpenOnly(false) }] : []}
        onOpenPanel={() => setOpenOnly((value) => !value)}
        filtersLabel={t('list.filters')}
        removeLabel={(label) => t('list.removeFilter', { values: { filter: label } })}
      />

      {board.data.totalCount === 0 ? (
        <DealsBoardEmpty />
      ) : (
        <KanbanBoard
          columns={columns}
          cardLabel={(id) => cardName.get(id) ?? id}
          labels={{
            moveTo: (card, column) => t('deal.moveTo', { values: { card, column } }),
            moveCard: t('deal.moveCard'),
            moving: t('deal.moving'),
            reverted: (reason) => t('deal.reverted', { values: { reason } }),
            emptyColumn: (column) => t('deal.emptyColumn', { values: { column } }),
            noTotal: t('deal.noTotal'),
          }}
          onMove={async (dealId, stageId) => {
            // Throws on refusal, and the board puts the card back with the
            // server's own sentence. A close that needs a reason arrives here
            // as a rejection naming the rule.
            await move.mutateAsync({ dealId, stageId });
          }}
        />
      )}

      {/* Where the pipeline leaks (F-100).
          
          On the board rather than on a screen of its own, because it is an
          analysis OF this pipeline and the selector above already chooses
          which one. A funnel somebody has to go and find is a funnel nobody
          checks. */}
      <PipelineFunnelPanel pipelineId={pipelineId ?? board.data.pipelineId} />
      {/* Closed deals by segment, and why they were won or lost (G-207, G-209, G-212). */}
      <DealAnalysisPanel />
    </div>
  );
}

function DealsBoardEmpty(): JSX.Element {
  const t = useT();
  return (
    <EmptyState
      reason="no-records"
      illustration="Target"
      title={t('deal.empty.title')}
      body={t('deal.empty.body')}
      primary={{ label: t('empty.action.newDeal'), icon: 'Plus', href: '/deals' }}
    />
  );
}

function DealsBoardLoading(): JSX.Element {
  const t = useT();
  return (
    <div className="flex w-full flex-col gap-4" role="status" aria-live="polite" aria-busy>
      <span className="sr-only">{t('state.loading')}</span>
      <Skeleton className="h-line-h2 w-block-xl" />
      <div className="flex gap-3">
        {Array.from({ length: 4 }, (_, column) => (
          <div key={column} className="flex w-panel-wide flex-col gap-2">
            {Array.from({ length: 3 }, (_, card) => (
              <Skeleton key={card} className="h-block-sm w-full" />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

function DealsBoardError({ error, retry }: { error: unknown; retry: () => void }): JSX.Element {
  return (
    <ErrorState
      title="The board could not be loaded"
      body={error instanceof Error ? error.message : 'The pipeline did not load.'}
      onRetry={retry}
    />
  );
}

export const screen = defineScreen({
  id: 'SC-12',
  route: '/deals',
  surface: 'page',
  title: 'Deals',
  // F-042 as well as F-041: the board draws one pipeline, and the selector
  // above it is the only place a person picks between several. A screen that
  // renders a capability and does not declare it is a capability nothing can
  // find.
  features: ['F-040', 'F-041', 'F-042', 'F-043', 'F-100'],
  entitlement: 'F-040',
  reachedBy: [{ kind: 'nav', item: 'deals' }],
  components: ['KanbanBoard', 'FilterChips'],
  // Funnel is a local element rather than one of the shared components in
  // 06_Component_Library, so it is declared here - but declared, because a
  // screen that renders something nothing can find is a screen whose contents
  // no check is watching.
  elements: ['DealsBoard', 'Funnel'],
  states: {
    empty: DealsBoardEmpty,
    loading: DealsBoardLoading,
    error: DealsBoardError,
  },
  component: DealsBoardScreen,
});

export default screen;
